import { useRef, useState } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Image, Save, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { downscaleImage } from '../../lib/imageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const galleryCategories = [
  { value: 'resin', label: 'Resin Art' },
  { value: 'jewelry', label: 'Jewelry' },
  { value: 'textiles', label: 'Textiles' },
  { value: 'woodwork', label: 'Woodwork' },
  { value: 'ceramics', label: 'Ceramics' },
  { value: 'home-decor', label: 'Home Decor' },
  { value: 'fine-art', label: 'Fine Art' },
  { value: 'events', label: 'Events' },
];

export default function GalleryItemForm({ item, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    title: item?.title || '',
    image_url: item?.image_url || '',
    description: item?.description || '',
    category: item?.category || 'home-decor',
    artisan_name: item?.artisan_name || '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Image too large — max 25 MB.');
      return;
    }
    setIsUploading(true);
    try {
      // Client-side downscale so photos from phones/cameras upload cleanly.
      let uploadFile = file;
      try {
        uploadFile = await downscaleImage(file);
      } catch (_) {
        uploadFile = file;
      }
      const form = new FormData();
      form.append('file', uploadFile);
      const token = localStorage.getItem('ams_admin_token');
      const res = await fetch(`${API}/admin/gallery/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await res.json();
      setFormData((prev) => ({ ...prev, image_url: data.url }));
      setPreviewError(false);
      toast.success('Image uploaded — it will live in persistent storage.');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.image_url.trim()) {
      toast.error('Image URL is required');
      return;
    }

    setIsSaving(true);

    await onSave({
      title: formData.title.trim(),
      image_url: formData.image_url.trim(),
      description: formData.description.trim() || null,
      category: formData.category,
      artisan_name: formData.artisan_name.trim() || null,
    });

    setIsSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Image Preview */}
      <div className="space-y-2">
        <Label>Image Preview</Label>
        <div className="aspect-video bg-muted rounded-lg overflow-hidden border">
          {formData.image_url && !previewError ? (
            <img
              src={formData.image_url}
              alt="Preview"
              className="w-full h-full object-cover"
              onError={() => setPreviewError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <Image className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">{previewError ? 'Failed to load image' : 'Enter image URL to preview'}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="gallery-image-url">Image URL *</Label>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleUpload}
              className="hidden"
              data-testid="gallery-file-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              data-testid="gallery-upload-btn"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Uploading&hellip;
                </>
              ) : (
                <>
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  Upload from computer
                </>
              )}
            </Button>
          </div>
        </div>
        <Input
          id="gallery-image-url"
          placeholder="https://example.com/image.jpg or upload above"
          value={formData.image_url}
          onChange={(e) => {
            setFormData(prev => ({ ...prev, image_url: e.target.value }));
            setPreviewError(false);
          }}
          required
          data-testid="gallery-image-url"
        />
        <p className="text-xs text-muted-foreground">
          Uploads go to persistent storage and survive redeploys. External URLs work too.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="gallery-title">Title *</Label>
        <Input
          id="gallery-title"
          placeholder="Beautiful Handmade Creation"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          required
          data-testid="gallery-title"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="gallery-category">Category *</Label>
        <Select
          value={formData.category}
          onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
        >
          <SelectTrigger id="gallery-category" data-testid="gallery-category">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {galleryCategories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="gallery-description">Caption / Description</Label>
        <Textarea
          id="gallery-description"
          placeholder="A brief description of this artwork..."
          rows={2}
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          data-testid="gallery-description"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="gallery-artisan">Artisan Name (optional)</Label>
        <Input
          id="gallery-artisan"
          placeholder="Artist's name"
          value={formData.artisan_name}
          onChange={(e) => setFormData(prev => ({ ...prev, artisan_name: e.target.value }))}
          data-testid="gallery-artisan"
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSaving} data-testid="gallery-save-btn">
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {item ? 'Save Changes' : 'Add Image'}
            </>
          )}
        </Button>
      </DialogFooter>
    </form>
  );
}
